<template>
  <div class="overview">
    <v-card flat>
      <v-card-text>
        <v-radio-group v-model="documentMode" row class="mb-4">
          <v-radio label="All" value="all" />
          <v-radio label="Specific Documents" value="specific" />
        </v-radio-group>
        <div v-if="documentMode === 'specific' && selectedItems.length" class="mb-2">
          <v-chip
            v-for="item in selectedItems"
            :key="item.id"
            small
            class="ma-1"
            close
            @click:close="removeSelection(item.id)"
          >
            {{ item.serviceRequestType }}{{ item.esvReference }}
          </v-chip>
        </div>
        <DataTable
          ref="esvTable"
          :key="tableKey"
          :optionsPassed="options"
          :totalEsvs="totalEsvs"
          :esvs="esvs"
          :searchPassed="search"
          :headerSelects="headerSelects"
          :headersTable="headersTable"
          v-on:getEsvs="getEsvs"
          :loading="loading"
          :allowIgnoreServiceLimitTIme="false"
          :selectedItems="selectedItems"
          @update:selectedItems="selectedItems = $event"
          :documentMode="documentMode"
          :organisationDisabled="organisationDisabled"
        >
          <template #title> Create Rule </template>

          <template #headerButtons>
            <v-btn color="primary" @click="saveRule(isEdit)">
              {{ isEdit ? "Update Rule" : "Add Rule" }}
            </v-btn>
            <v-btn class="mr-2 ml-2" small @click="$router.back()">
              Cancel
            </v-btn>
          </template>
        </DataTable>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
// UI uses `documentType`, backend uses `serviceRequestType`

import { ExportType, StatusCode } from "@/enums";
import DataTable from "./CreateRuleTable.vue";

export default {
  name: "AddRule",
  components: {
    DataTable,
  },
  props: {
    ruleId: {
      type: [String, Number],
      Number,
      required: false,
    },
  },
  data: () => ({
    initialEsvsLoaded: false,
    initialModeHandled: false,
    organisationDisabled: false,
    initialSelectionApplied: false,
    documentMode: "all",
    esvs: [],
    tableKey: Date.now(),
    search: {
      organisation: null,
      country: null,
      sbmProfile: null,
      serviceGrade: null,
      productGroup: null,
      productSubGroup: null,
      infoLevel: null,
      activeDateFrom: null,
      activeDateTo: null,
      documentType: null,
    },

    options: {
      /*itemsPerPage: 15*/
    },
    totalEsvs: null,

    selectedItems: [],

    headerSelects: {
      status: [],
      organisation: [],
      country: [],
      sbmProfile: [],
      serviceGrade: [],
      productGroup: [],
      productSubGroup: [],
      documentType: [],

      infoLevel: [],
    },
    loading: false,
    downloadLoading: false,
  }),
  methods: {
    removeSelection(id) {
      const index = this.selectedItems.findIndex(i => i.id === id);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }

      const row = this.esvs.find(e => e.id === id);
      if (row) {
        row.selected = false;
      }
    },
    async getEsvs(tableSearch) {
      try {
        this.loading = true;
        this.search = tableSearch.search;
        this.options = tableSearch.options;

        const resp = await this.$api.getEsvRnsOverview(tableSearch);
        this.esvs = resp.data.esvs.map(e => ({
          ...e,
          documentType: e.serviceRequestType,
        }));
        this.totalEsvs = resp.data.totalEsvs;
      } catch (err) {
        console.error("❌ Fout bij getEsvs:", err);
        this.$toast.error("Fout bij ophalen ESVs");
      } finally {
        this.loading = false;
      }
    },

    async loadRule(id) {
      this.loading = true;
      try {
        const resp = await this.$api.getRuleById(id);
        const rule = resp.data;
        this.loadedRule = rule;

        const docValue = (rule.documentNrs || "").trim().toUpperCase();     

        if (docValue === "ALL") {                                           
          this.documentMode = "all";                                        
        } else {                                                            
          this.documentMode = "specific";                                   
        }                                                                   

        Object.assign(this.search, {
          organisation: rule.organisationId,
          country: Array.isArray(rule.countryId)
            ? rule.countryId
            : rule.countryId
            ? [rule.countryId]
            : [],
          sbmProfile: Array.isArray(rule.sbmProfileId)
            ? rule.sbmProfileId
            : rule.sbmProfileId
            ? [rule.sbmProfileId]
            : [],
          serviceGrade: Array.isArray(rule.serviceGradeId)
            ? rule.serviceGradeId
            : rule.serviceGradeId
            ? [rule.serviceGradeId]
            : [],
          productGroup: Array.isArray(rule.productGroupId)
            ? rule.productGroupId
            : rule.productGroupId
            ? [rule.productGroupId]
            : [],
          documentType: Array.isArray(rule.serviceRequestTypeId)
            ? rule.serviceRequestTypeId
            : rule.serviceRequestTypeId
            ? [rule.serviceRequestTypeId]
            : [],
          infoLevel: rule.infoLevel,
          activeDateFrom: rule.activeDateFrom,
          activeDateTo: rule.activeDateTo,
        });

        await this.getEsvs({ search: this.search, options: this.options });
        await this.$nextTick();

      }catch (err) {
        console.error("❌ Fout bij loadRule:", err);
        this.$toast.error("Fout bij het laden van de regel");
      } finally {
        this.loading = false;
      }
    },

    setSelectedESVs(rule) {
      const docValue = (rule.documentNrs || "").trim().toUpperCase();

      if (docValue === "ALL") {
        this.selectedItems = [...this.esvs];
        this.esvs.forEach((e) => (e.selected = true));
        console.log("✅ Alle ESV's geselecteerd (ALL)");
      } else {
        const selectedDocs = docValue.split(",").map((s) => s.trim());
        this.selectedItems = this.esvs.filter((item) => {
          const ref = item.esvReference?.trim();
          const full = `${item.serviceRequestType}${ref}`.trim();
          return selectedDocs.includes(ref) || selectedDocs.includes(full);
        });
        this.esvs.forEach((e) => (e.selected = this.selectedItems.includes(e)));
        console.log("✅ Geselecteerde specifieke ESV's:", this.selectedItems);
      }
    },

    prepareRulePayload() {
      const itemsToUse = this.selectedItems || [];

      if (this.documentMode === "all") {
        // oude logica blijft intact
        return {
          OrganisationId: parseInt(this.search.organisation, 10) || null,
          CountryIds: Array.isArray(this.search.country)
            ? this.search.country.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          SBMProfileIds: Array.isArray(this.search.sbmProfile)
            ? this.search.sbmProfile.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          ServiceGradeIds: Array.isArray(this.search.serviceGrade)
            ? this.search.serviceGrade.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          ProductGroupIds: Array.isArray(this.search.productGroup)
            ? this.search.productGroup.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          ServiceRequestTypeIds: Array.isArray(this.search.documentType)
            ? this.search.documentType.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          InfoLevel: this.search.infoLevel || null,
          ActiveDateFrom: this.search.activeDateFrom || null,
          ActiveDateTo: this.search.activeDateTo || null,
          DocumentNrs: "ALL"
        };
      } else {
        // specific docs: stuur labels naar backend
        const serviceGradeNames = [...new Set(itemsToUse.map(d => d.serviceGrade).filter(Boolean))];
        const productGroupNames = [...new Set(itemsToUse.map(d => d.productGroup).filter(Boolean))];
        const documentTypeNames = [...new Set(itemsToUse.map(d => d.documentType).filter(Boolean))];

        return {
          OrganisationId: parseInt(this.search.organisation, 10) || null,
          CountryIds: Array.isArray(this.search.country)
            ? this.search.country.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          SBMProfileIds: Array.isArray(this.search.sbmProfile)
            ? this.search.sbmProfile.map(v => parseInt(v, 10)).filter(v => !isNaN(v))
            : [],
          ServiceGradeNames: serviceGradeNames,
          ProductGroupNames: productGroupNames,
          DocumentTypeNames: documentTypeNames,
          InfoLevel: this.search.infoLevel || null,
          ActiveDateFrom: this.search.activeDateFrom || null,
          ActiveDateTo: this.search.activeDateTo || null,
          DocumentNrs: itemsToUse
            .map(d => `${d.serviceRequestType}${d.esvReference}`)
            .filter(Boolean)
            .join(",")
        };
      }
    },
  

    async saveRule(isEdit = false) {
      const isValid = this.$refs.esvTable.validateForm();

      if (!isValid) {
        return;
      }

      const payload = this.prepareRulePayload();

      if (
        this.documentMode === "specific" &&
        (!this.selectedItems || !this.selectedItems.length)
      ) {
        this.$toast.error("At least one document must be selected");
        return;
      }

      try {
        this.downloadLoading = true;

        if (isEdit) {
          await this.$api.updateRule(this.ruleId, payload);
          this.$toast.success("Rule updated successfully!");
        } else {
          await this.$api.addRule(payload);
          this.$toast.success("Rule added successfully!");
        }

        this.$router.push({ name: "CommunicationRules" });
      } catch (err) {
        console.error(err);
        this.$toast.error("Save failed");
      } finally {
        this.downloadLoading = false;
      }
    },

    // helper toggles als je custom checkboxes gebruikt:
    isRowSelected(row) {
      return (
        Array.isArray(this.selectedItems) &&
        this.selectedItems.some((i) => i.id === row.id)
      );
    },
    toggleRowSelection(row) {
      if (!this.selectedItems) this.selectedItems = [];
      const idx = this.selectedItems.findIndex((i) => i.id === row.id);
      if (idx === -1) this.selectedItems.push(row);
      else this.selectedItems.splice(idx, 1);
      row.selected = idx === -1;
    },

    download() {
      this.downloadLoading = true;
      this.$api
        .downloadOverview(
          ExportType.excel,
          StatusCode.inProgress,
          this.search,
          true
        )
        .finally(() => (this.downloadLoading = false));
    },
    applySelectedEsvs() {
      const selectedDocs = (this.loadedRule?.documentNrs || "")
        .split(",")
        .map((s) => s.trim());

      this.esvs.forEach((item) => {
        const key = `${item.serviceRequestType}${
          item.esvReference || ""
        }`.trim();
        item.selected = selectedDocs.includes(key);
      });

      this.selectedItems = this.esvs.filter((i) => i.selected);
      console.log("✅ Aangevinkte ESVs:", this.selectedItems);
    },
  },
  computed: {
    isOnlyRNS() {
      return (
        Array.isArray(this.search.documentType) &&
        this.search.documentType.length === 1 &&
        this.search.documentType.includes(24)
      );
    },
   headersTable() {
    const headers = [
      {
        text: "Type",
        value: "documentType",
        type: "multiselect",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Dossier",
        value: "esvReference",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Description",
        value: "subject",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Service Grade",
        value: "serviceGrade",
        type: "multiselect",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Product Group",
        value: "productGroup",
        type: "multiselect",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Product Subgroup",
        value: "productSubGroup",
        type: "select",
        class: "text-subtitle-1 font-weight-bold",
      },
    ];

    if (this.isOnlyRNS) {
      return headers.filter(h => h.value !== "serviceGrade");
    }

    return headers;
  },

  isEdit() {
    return !!this.$route.params.ruleId;
  },
  },
  async mounted() {
    this.$api.getAllowedInfoLevels().then(resp => {
      this.headerSelects.infoLevel = resp.data.map(level => ({
        value: level,
        text: level
      }));
    });
    if (this.$refs.esvTable && this.$refs.esvTable.getOrganisations) {
      await this.$refs.esvTable.getOrganisations();
    }

    const userShorthand = (this.$store.state.user?.organisation ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const userRole = this.$store.state.user?.role;

    if ((userRole === "101" || userRole === "104") && userShorthand) {
      if (!this.headerSelects.organisation?.length) {
        console.warn(
          "⚠️ headerSelects.organisation nog leeg na getOrganisations call"
        );
      }

      const orgMatch = this.headerSelects.organisation.find((o) => {
        const shorthand = (o.shorthand ?? "").toString().trim().toUpperCase();
        return shorthand && shorthand === userShorthand;
      });

      if (orgMatch) {
        this.search.organisation = orgMatch.value;
        const isDenv = userShorthand === "DENV";
        this.organisationDisabled = !isDenv;
        await this.$nextTick();
      } else {
        console.warn(
          "⚠️ Geen match voor user shorthand:",
          userShorthand,
          "— beschikbaar:",
          this.headerSelects.organisation
        );
      }
    }

    if (this.isEdit) {
      await this.$refs.esvTable.loadHeaderSelects?.();
      await this.loadRule(this.ruleId);
    }
  },

  watch: {
    documentMode(newVal, oldVal) {
      if (newVal === "all") {
        this.selectedItems = Array.isArray(this.esvs) ? [...this.esvs] : [];
        this.initialModeHandled = true;
        return;
      }

      if (newVal === "specific") {
        if (this.isEdit && this.loadedRule && this.initialEsvsLoaded) {
          this.$nextTick(() => this.setSelectedESVs(this.loadedRule));
          this.initialModeHandled = true;
          return;
        }

        if (!this.isEdit) {
          if (oldVal === "all" || this.initialModeHandled) {
            this.selectedItems = [];
            this.initialModeHandled = true;
          }
        }
      }
    },
    
    esvs(newList) {
      if (!Array.isArray(newList)) return;

      if (!this.initialEsvsLoaded && newList.length > 0) {
        this.initialEsvsLoaded = true;
      }

      if (!this.isEdit && this.documentMode === "all") {
        this.selectedItems = [...newList];
      }

      if (
        this.isEdit &&
        this.documentMode === "specific" &&
        this.loadedRule &&
        !this.initialSelectionApplied
      ) {
        this.$nextTick(() => {
          this.setSelectedESVs(this.loadedRule);
          this.initialSelectionApplied = true;
        });
      }
    },

    "$route.params.ruleId": {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadRule(newId);
        } else {
          // nieuw formulier
          this.search = {
            organisation: null,
            country: null,
            sbmProfile: null,
            serviceGrade: null,
            productGroup: null,
            productSubGroup: null,
            infoLevel: null,
            activeDateFrom: null,
            activeDateTo: null,
            documentType: null,
          };
        }
      },
    },
  },
};
</script>